import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';

// Validation schema
const schema = yup.object().shape({
  customer: yup.string().required('Customer is required'),
  primaryContact: yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    phoneNumbers: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Phone type is required'),
        number: yup.string().required('Phone number is required'),
      })
    ).min(1, 'At least one phone number is required'),
    emails: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Email type is required'),
        address: yup.string().email('Invalid email format').required('Email address is required'),
      })
    ).min(1, 'At least one email address is required'),
  }),
  serviceLocation: yup.object().shape({
    streetAddress: yup.string().required('Street address is required'),
    city: yup.string().required('City is required'),
    stateProvince: yup.string().required('State/Province is required'),
    zipPostalCode: yup.string().required('ZIP/Postal code is required'),
  }),
  jobCategory: yup.string().required('Job category is required'),
  jobDescription: yup.string().required('Job description is required'),
  
  assignedTechs: yup.array().min(1, 'At least one technician must be assigned'),
  status: yup.string().required('Status is required'),
  start_date: yup.string().required('Start Date is required'),
  arrival_start_time: yup.string().required('Arrival Start Time is required'),
  arrival_end_time: yup.string().required('Arrival End Time is required'),
  priority: yup.string().required('Priority is required'),
});

const JobForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingJob, setIsLoadingJob] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [lineItems, setLineItems] = useState([]);
  const [driverLaborTimes, setDriverLaborTimes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments] = useState([]);
  const [companyWorkForce, setCompanyWorkForce] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  

  // Phone type options
  const phoneTypeOptions = [
    { value: 'mobile', label: 'Mobile' },
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Work' },
    { value: 'other', label: 'Other' },
  ];

  // Email type options
  const emailTypeOptions = [
    { value: 'Personal', label: 'Personal' },
    { value: 'Work', label: 'Work' },
    { value: 'Other', label: 'Other' },
  ];

  // Custom styles for react-select
  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '42px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: '#9ca3af',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#009efb' : state.isFocused ? '#f3f4f6' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:active': {
        backgroundColor: '#0089db',
      },
    }),
  };

  // Phone number formatting function
  const handlePhoneNumberChange = (e, index) => {
    const input = e.target.value;
    const cleaned = input.replace(/\D/g, '');
    let formatted = '';
    if (cleaned.length > 0) formatted = '(' + cleaned.substring(0, 3);
    if (cleaned.length > 3) formatted += ') ' + cleaned.substring(3, 6);
    if (cleaned.length > 6) formatted += '-' + cleaned.substring(6, 10);
    setValue(`primaryContact.phoneNumbers.${index}.number`, formatted);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      customer: '',
      primaryContact: {
        firstName: '',
        lastName: '',
        phoneNumbers: [{ type: 'mobile', number: '', ext: '' }],
        emails: [{ type: 'Personal', address: '' }],
      },
      serviceLocation: {
        locationName: '',
        isGatedProperty: false,
        streetAddress: '',
        unit: '',
        city: '',
        stateProvince: '',
        zipPostalCode: '',
      },
      jobCategory: '',
      jobDescription: '',
      start_date: '',
      status: '',
      arrival_start_time: '',
      arrival_end_time: '',
      estimation_duration_h: '',
      estimation_duration_m: '',
      priority: '',
      note_for_tech: '',
      completion_note: '',
      assignedTechs: [],
      tasks: [],
      notes: [],
      products: [],
      driverLaborTimes: [],
      expenses: [],
    },
  });

  // Update form values when state changes
  useEffect(() => {
    setValue('products', lineItems);
  }, [lineItems, setValue]);

  useEffect(() => {
    setValue('driverLaborTimes', driverLaborTimes);
  }, [driverLaborTimes, setValue]);

  useEffect(() => {
    setValue('expenses', expenses);
  }, [expenses, setValue]);

  const [showTaskInput, setShowTaskInput] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  
  const [newTask, setNewTask] = useState('');
   const [newNote, setNewNote] = useState('');

  // Load customers from localStorage on component mount
  useEffect(() => {
    const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
    setCustomers(storedCustomers);
  }, []);

  useEffect(() => {
    const loadJob = async () => {
      try {
        setIsLoadingJob(true);
        if (id) {
          
          const jobs = JSON.parse(localStorage.getItem('jobs') || '[]');
          const job = jobs.find((j) => j.id === id);
           
          if (job) {
            const jobData = {
              ...job,
              primaryContact: job.primaryContact || {
                firstName: '',
                lastName: '',
                phoneNumbers: [{ type: 'mobile', number: '', ext: '' }],
                emails: [{ type: 'Personal', address: '' }],
              },
              serviceLocation: job.serviceLocation || {
                locationName: '',
                isGatedProperty: false,
                streetAddress: '',
                unit: '',
                city: '',
                stateProvince: '',
                zipPostalCode: '',
              },
              assignedTechs: Array.isArray(job.assignedTechs) ? job.assignedTechs : [],
              tasks: Array.isArray(job.tasks) ? job.tasks : [],
              notes: Array.isArray(job.notes) ? job.notes : [],
              products: Array.isArray(job.products) ? job.products : [],
              driverLaborTimes: Array.isArray(job.driverLaborTimes) ? job.driverLaborTimes : [],
              expenses: Array.isArray(job.expenses) ? job.expenses : [],
            };
            
            reset(jobData);
            setLineItems(jobData.products || []);
            setDriverLaborTimes(jobData.driverLaborTimes || []);
            setExpenses(jobData.expenses || []);
          } else {
            console.error('Job not found with ID:', id);
            toast.error('Job not found');
            navigate('/jobs');
          }
        }
      } catch (error) {
        console.error('Error loading job:', error);
        toast.error('Failed to load job data');
        navigate('/jobs');
      } finally {
        setIsLoadingJob(false);
      }
    };
    loadJob();
  }, [id, reset, navigate]);

  // Load company workforce and expense categories
  useEffect(() => {
    const storedWorkForce = JSON.parse(localStorage.getItem('companyWorkForce') || '[]');
    const storedCategories = JSON.parse(localStorage.getItem('expenseCategories') || '[]');
    setCompanyWorkForce(storedWorkForce);
    setExpenseCategories(storedCategories);
  }, []);

  const handleCustomerSearch = (searchText) => {
    setValue('customer', searchText);
    if (searchText.trim() === '') {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
      return;
    }
    const filtered = customers.filter(
      (customer) =>
        customer.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
        customer.accountNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
        customer.primaryContact?.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
        customer.primaryContact?.lastName?.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredCustomers(filtered);
    setShowCustomerDropdown(true);
  };

  const handleCustomerSelect = (customer) => {
    setValue('customer', customer.customerName);
    setValue('primaryContact.firstName', customer.primaryContact?.firstName || '');
    setValue('primaryContact.lastName', customer.primaryContact?.lastName || '');
    setValue('primaryContact.phoneNumbers', customer.primaryContact?.phoneNumbers || [{ type: 'mobile', number: '', ext: '' }]);
    setValue('primaryContact.emails', customer.primaryContact?.emails || [{ type: 'Personal', address: '' }]);
    if (customer.serviceLocations?.[0]) {
      const location = customer.serviceLocations[0];
      setValue('serviceLocation.streetAddress', location.streetAddress || '');
      setValue('serviceLocation.city', location.city || '');
      setValue('serviceLocation.stateProvince', location.stateProvince || '');
      setValue('serviceLocation.zipPostalCode', location.zipPostalCode || '');
    }
    setShowCustomerDropdown(false);
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const currentTasks = watch('tasks') || [];
      setValue('tasks', [...currentTasks, { id: Date.now(), text: newTask, completed: false }]);
      setNewTask('');
      setShowTaskInput(false);
    }
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const currentNotes = watch('notes') || [];
      setValue('notes', [...currentNotes, { id: Date.now(), text: newNote, completed: false }]);
      setNewNote('');
      setShowNoteInput(false);
    }
  };

  const addPhoneNumber = () => {
    const currentPhones = watch('primaryContact.phoneNumbers') || [];
    setValue('primaryContact.phoneNumbers', [...currentPhones, { type: 'mobile', number: '', ext: '' }]);
  };

  const addEmail = () => {
    const currentEmails = watch('primaryContact.emails') || [];
    setValue('primaryContact.emails', [...currentEmails, { type: 'Personal', address: '' }]);
  };

  const handleRemovePhoneNumber = (index) => {
    const currentPhones = watch('primaryContact.phoneNumbers') || [];
    if (currentPhones.length > 1) {
      setValue('primaryContact.phoneNumbers', currentPhones.filter((_, i) => i !== index));
    } else {
      toast.error('At least one phone number is required');
    }
  };

  const handleRemoveEmail = (index) => {
    const currentEmails = watch('primaryContact.emails') || [];
    if (currentEmails.length > 1) {
      setValue('primaryContact.emails', currentEmails.filter((_, i) => i !== index));
    } else {
      toast.error('At least one email address is required');
    }
  };

  const onSubmitForm = async (data) => {
    try {
      setIsLoading(true);
      const jobs = JSON.parse(localStorage.getItem('jobs') || '[]');
      const jobData = {
        ...data,
        id: id || Date.now().toString(),
        createdAt: id ? data.createdAt : new Date().toISOString(),
        createdBy: id ? data.createdBy : user?.id || 'system',
        updatedAt: new Date().toISOString(),
        updatedBy: user?.id || 'system',
        status: data.status || 'pending',
        start_date: data.start_date,
        arrival_start_time: data.arrival_start_time,
        arrival_end_time: data.arrival_end_time,
        estimation_duration_h: data.estimation_duration_h,
        estimation_duration_m: data.estimation_duration_m,
        priority: data.priority,
        note_for_tech: data.note_for_tech,
        completion_note: data.completion_note,
        notes: data.notes || [],
        tasks: data.tasks || [],
        products: data.products || [],
        driverLaborTimes: data.driverLaborTimes || [],
        expenses: data.expenses || [],
        assignedTechs: data.assignedTechs || [],
        customer: data.customer,
        primaryContact: data.primaryContact,
        serviceLocation: data.serviceLocation,
        jobCategory: data.jobCategory,
        jobDescription: data.jobDescription
      };

      if (id) {
        const index = jobs.findIndex((job) => job.id === id);
        if (index !== -1) jobs[index] = jobData;
      } else {
        jobs.push(jobData);
      }
      localStorage.setItem('jobs', JSON.stringify(jobs));
      toast.success(id ? 'Job updated successfully' : 'Job created successfully');
      navigate('/jobs');
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLineItem = (value) => {
    let description = '';
    if (value && typeof value.length !== 'undefined') {
      description = value
    } 
    
    const newLineItem = {
      id: Date.now(),
      description: description,
      warehouse: '',
      quantity: 0,
      rate: 0,
      total: 0,
      cost: 0,
      margin: 0,
      tax: '',
    };
    
    setLineItems([...lineItems, newLineItem]);
  };

  const handleRemoveLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const handleCopyLineItem = (id) => {
    const itemToCopy = lineItems.find((item) => item.id === id);
    if (itemToCopy) {
      const newItem = { ...itemToCopy, id: Date.now() };
      setLineItems([...lineItems, newItem]);
    }
  };

  const calculateTotal = (quantity, rate) => {
    return (parseFloat(quantity) || 0) * (parseFloat(rate) || 0);
  };

  const handleLineItemChange = (id, field, value) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updatedItem.total = calculateTotal(
              field === 'quantity' ? value : item.quantity,
              field === 'rate' ? value : item.rate
            );
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const calculateTotals = () => {
    const productsTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const driverLaborTotal = driverLaborTimes.reduce((sum, item) => 
      sum + (item.billable ? (parseFloat(item.total) || 0) : 0), 0);
    const expensesTotal = expenses.reduce((sum, item) => 
      sum + (item.billable ? (parseFloat(item.amount) || 0) : 0), 0);
    const paymentsTotal = payments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const jobTotal = productsTotal + driverLaborTotal + expensesTotal;
    const totalDue = jobTotal - paymentsTotal;
    const jobCost = lineItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
    const grossProfit = jobTotal - jobCost;
    const grossProfitPercentage = jobTotal > 0 ? (grossProfit / jobTotal) * 100 : 0;

    return {
      productsTotal,
      driverLaborTotal,
      expensesTotal,
      jobTotal,
      paymentsTotal,
      totalDue,
      jobCost,
      grossProfit,
      grossProfitPercentage,
    };
  };

  const totals = calculateTotals();

  // Driver & Labor Times handlers
  const handleAddDriverLaborTime = () => {
    const newTime = {
      id: Date.now(),
      tech: '',
      date: '',
      driveStartTime: '',
      driveEndTime: '',
      laborStartTime: '',
      laborEndTime: '',
      driverHours: 0,
      driverMinutes: 0,
      laborHours: 0,
      laborMinutes: 0,
      driverRate: 0,
      laborRate: 0,
      billable: false,
      total: 0,
      cost: 0,
      margin: 0
    };
    setDriverLaborTimes([...driverLaborTimes, newTime]);
  };

  const handleRemoveDriverLaborTime = (id) => {
    setDriverLaborTimes(driverLaborTimes.filter(item => item.id !== id));
  };

  const handleDriverLaborTimeChange = (id, field, value) => {
    setDriverLaborTimes(driverLaborTimes.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // Calculate hours and minutes if time fields changed
        if (field === 'driveStartTime' || field === 'driveEndTime') {
          const start = new Date(`2000-01-01T${updatedItem.driveStartTime}`);
          const end = new Date(`2000-01-01T${updatedItem.driveEndTime}`);
          if (!isNaN(start) && !isNaN(end)) {
            const diff = end - start;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            updatedItem.driverHours = hours;
            updatedItem.driverMinutes = minutes;
          }
        }
        
        if (field === 'laborStartTime' || field === 'laborEndTime') {
          const start = new Date(`2000-01-01T${updatedItem.laborStartTime}`);
          const end = new Date(`2000-01-01T${updatedItem.laborEndTime}`);
          if (!isNaN(start) && !isNaN(end)) {
            const diff = end - start;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            updatedItem.laborHours = hours;
            updatedItem.laborMinutes = minutes;
          }
        }

        // Calculate total if billable
        if (updatedItem.billable) {
          const driverTotal = (updatedItem.driverHours + updatedItem.driverMinutes / 60) * updatedItem.driverRate;
          const laborTotal = (updatedItem.laborHours + updatedItem.laborMinutes / 60) * updatedItem.laborRate;
          updatedItem.total = driverTotal + laborTotal;
          updatedItem.cost = updatedItem.total * 0.7; // Assuming 30% margin
          updatedItem.margin = updatedItem.total - updatedItem.cost;
        }

        return updatedItem;
      }
      return item;
    }));
  };

  // Expenses handlers
  const handleAddExpense = () => {
    const newExpense = {
      id: Date.now(),
      date: '',
      category: '',
      purchasedFrom: '',
      amount: 0,
      billable: false,
      reimburseTo: '',
      notes: '',
      receipts: []
    };
    setExpenses([...expenses, newExpense]);
  };

  const handleRemoveExpense = (id) => {
    setExpenses(expenses.filter(item => item.id !== id));
  };

  const handleExpenseChange = (id, field, value) => {
    setExpenses(expenses.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const handleReceiptUpload = (id, files) => {
    setExpenses(expenses.map(item => {
      if (item.id === id) {
        return { ...item, receipts: [...item.receipts, ...files] };
      }
      return item;
    }));
  };

   const assignTechOptions = companyWorkForce.map(tech => ({
    value: `${tech.firstName} ${tech.lastName}`,
    label: `${tech.firstName} ${tech.lastName}`
  }));
      
  if (isLoadingJob) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{id ? 'Edit Job' : 'Create New Job'}</h1>
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-8">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 ">
        <div className="bg-white rounded-lg shadow-md p-6">
           <h2 className="text-xl font-semibold text-gray-800 mb-2">Details</h2>
           <hr className="border-t border-gray-200 mb-4" />
          {/* Customer Section */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Customer</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Type to search customers or create new..."
                {...register('customer', {
                  required: 'Customer is required',
                  onChange: (e) => {
                    handleCustomerSearch(e.target.value);
                    if (e.target.value) {
                      setValue('customer', e.target.value, { shouldValidate: true });
                    }
                  },
                })}
                onFocus={() => watch('customer') && setShowCustomerDropdown(true)}
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.customer ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.customer && <p className="mt-1 text-sm text-red-600">{errors.customer.message}</p>}
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      {customer.customerName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Primary Contact Section */}
          <div className="space-y-4 mt-6">
            <label className="block text-sm font-medium text-gray-700">Primary Contact</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  {...register('primaryContact.firstName')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.primaryContact?.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.primaryContact?.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.primaryContact.firstName.message}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Last Name"
                  {...register('primaryContact.lastName')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.primaryContact?.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.primaryContact?.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.primaryContact.lastName.message}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                  <button
                    type="button"
                    onClick={addPhoneNumber}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
                {watch('primaryContact.phoneNumbers')?.map((phone, index) => (
                  <div key={index} className="flex items-center gap-2 mb-3">
                    <div className="w-32">
                      <Select
                        name={`primaryContact.phoneNumbers.${index}.type`}
                        value={phoneTypeOptions.find((option) => option.value === watch(`primaryContact.phoneNumbers.${index}.type`))}
                        onChange={(selectedOption) => setValue(`primaryContact.phoneNumbers.${index}.type`, selectedOption?.value || '')}
                        options={phoneTypeOptions}
                        styles={customStyles}
                        placeholder="Select Type"
                        className={`react-select-container ${
                          errors.primaryContact?.phoneNumbers?.[index]?.type ? 'border-red-500' : 'border-gray-300'
                        }`}
                        classNamePrefix="react-select"
                      />
                      {errors.primaryContact?.phoneNumbers?.[index]?.type && (
                        <p className="mt-1 text-sm text-red-600">{errors.primaryContact.phoneNumbers[index].type.message}</p>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Phone Number"
                        {...register(`primaryContact.phoneNumbers.${index}.number`, {
                          required: 'Phone number is required',
                          onChange: (e) => handlePhoneNumberChange(e, index),
                        })}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.primaryContact?.phoneNumbers?.[index]?.number ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.primaryContact?.phoneNumbers?.[index]?.number && (
                        <p className="mt-1 text-sm text-red-600">{errors.primaryContact.phoneNumbers[index].number.message}</p>
                      )}
                    </div>
                    {index !== 0 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePhoneNumber(index)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-full"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
                  <button
                    type="button"
                    onClick={addEmail}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
                {watch('primaryContact.emails')?.map((email, index) => (
                  <div key={index} className="flex items-center gap-2 mb-3">
                    <div className="w-32">
                      <Select
                        name={`primaryContact.emails.${index}.type`}
                        value={emailTypeOptions.find((option) => option.value === watch(`primaryContact.emails.${index}.type`))}
                        onChange={(selectedOption) => setValue(`primaryContact.emails.${index}.type`, selectedOption?.value || '')}
                        options={emailTypeOptions}
                        styles={customStyles}
                        placeholder="Select Type"
                        className={`react-select-container ${
                          errors.primaryContact?.emails?.[index]?.type ? 'border-red-500' : 'border-gray-300'
                        }`}
                        classNamePrefix="react-select"
                      />
                      {errors.primaryContact?.emails?.[index]?.type && (
                        <p className="mt-1 text-sm text-red-600">{errors.primaryContact.emails[index].type.message}</p>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="email"
                        placeholder="Email Address"
                        {...register(`primaryContact.emails.${index}.address`, {
                          required: 'Email address is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.primaryContact?.emails?.[index]?.address ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.primaryContact?.emails?.[index]?.address && (
                        <p className="mt-1 text-sm text-red-600">{errors.primaryContact.emails[index].address.message}</p>
                      )}
                    </div>
                    {index !== 0 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(index)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-full"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Service Location Section */}
          <div className="space-y-4 mt-6">
            <label className="block text-sm font-medium text-gray-700">Service Location</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Location Name"
                  {...register('serviceLocation.locationName')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isGatedProperty"
                  {...register('serviceLocation.isGatedProperty')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isGatedProperty" className="ml-2 block text-sm text-gray-700">
                  Gated Property
                </label>
              </div>
              <div className="md:col-span-2">
                <input
                  type="text"
                  placeholder="Street Address"
                  {...register('serviceLocation.streetAddress')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.serviceLocation?.streetAddress ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.serviceLocation?.streetAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.serviceLocation.streetAddress.message}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Unit"
                  {...register('serviceLocation.unit')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="City"
                  {...register('serviceLocation.city')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.serviceLocation?.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.serviceLocation?.city && <p className="mt-1 text-sm text-red-600">{errors.serviceLocation.city.message}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="State/Province"
                  {...register('serviceLocation.stateProvince')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.serviceLocation?.stateProvince ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.serviceLocation?.stateProvince && (
                  <p className="mt-1 text-sm text-red-600">{errors.serviceLocation.stateProvince.message}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="ZIP/Postal Code"
                  {...register('serviceLocation.zipPostalCode')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.serviceLocation?.zipPostalCode ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.serviceLocation?.zipPostalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.serviceLocation.zipPostalCode.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Job Details Section */}
          <div className="space-y-4 mt-6">
            <label className="block text-sm font-medium text-gray-700">Job Details</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <select
                  {...register('jobCategory')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.jobCategory ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Job Category</option>
                  <option value="installation">Installation</option>
                  <option value="repair">Repair</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inspection">Inspection</option>
                </select>
                {errors.jobCategory && <p className="mt-1 text-sm text-red-600">{errors.jobCategory.message}</p>}
              </div>
              <div>
                <textarea
                  placeholder="Job Description"
                  {...register('jobDescription')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.jobDescription ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows="3"
                />
                {errors.jobDescription && <p className="mt-1 text-sm text-red-600">{errors.jobDescription.message}</p>}
              </div>
            </div>
          </div>
  
          {/* Assigned Technicians Section */}
          <div className="space-y-4 mt-6">
            <label className="block text-sm font-medium text-gray-700">Assigned Technicians</label>
            
            <Select
              isMulti
              options={assignTechOptions}
              value={watch('assignedTechs')?.map((tech) => ({ value: tech, label: tech }))}
              onChange={(selectedOptions) => setValue('assignedTechs', selectedOptions.map((option) => option.value))}
              className={`${errors.assignedTechs ? 'border-red-500' : 'border-gray-300'}`}
              classNamePrefix="select"
              placeholder="Select Technicians..."
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.assignedTechs ? '#ef4444' : '#d1d5db',
                  '&:hover': {
                    borderColor: errors.assignedTechs ? '#ef4444' : '#9ca3af',
                  },
                }),
              }}
            />
            {errors.assignedTechs && <p className="mt-1 text-sm text-red-600">{errors.assignedTechs.message}</p>}
          </div>
  
        </div>

         <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Job Info</h2>
           <hr className="border-t border-gray-200 mb-4" />
            <div className="space-y-4 mt-6">
              <label className="block text-sm font-medium text-gray-700">Current Status</label>
               <select
                  {...register('status')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.status ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Status</option>
                  <option value="unscheduled">Unscheduled</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="review_charging">Review-Charging?</option>
                  <option value="wait_for_uponor">Wait For Uponor</option>
                   <option value="deferred_work">Deferred Work</option>
                </select>
                {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
            </div>
            <div className="space-y-4 mt-6">
              <label className="block text-sm font-medium text-gray-700">Start & End Dates</label>
               <input
                  type="date"
                  {...register('start_date')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.start_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                  
                {errors.start_date && <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>}
            </div>
          <div className="space-y-4 mt-6  ">
           <label className="block text-sm font-medium text-gray-700">Arrival Time Window</label>
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
               <label className="block text-sm font-medium text-gray-700">Start Time</label>
               <input
                  type="time"
                  {...register('arrival_start_time')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.arrival_start_time ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                  
                {errors.arrival_start_time && <p className="mt-1 text-sm text-red-600">{errors.arrival_start_time.message}</p>}
            </div>
                 
               <div>
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  {...register('arrival_end_time')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.arrival_end_time ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                  
                {errors.arrival_end_time && <p className="mt-1 text-sm text-red-600">{errors.arrival_end_time.message}</p>}
            </div>
            </div>
          </div>
          <div className="space-y-4 mt-6  ">
            <label className="block text-sm font-medium text-gray-700">Estimated Duration</label>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
                  <input type="number" className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" {...register('estimation_duration_h')}/>h
                  <input type="number" className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" {...register('estimation_duration_m')}/>m
                   
              </div>
          </div>

           <div className="space-y-4 mt-6  ">
            <label className="block text-sm font-medium text-gray-700"> Job Priority</label>
            <select
            {...register('priority')}
             className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.priority ? 'border-red-500' : 'border-gray-300'
                  }`}
                
            >
              <option value="">Select Job Priority</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="low">Low</option>
              
            </select>
             {errors.priority && <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>}
           </div>
            <div className="space-y-4 mt-6  ">
             <label className="block text-sm font-medium text-gray-700"> Notes For Techs</label>
             <textarea
              {...register('note_for_tech')}
              className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.note_fo_tech ? 'border-red-500' : 'border-gray-300'
                  }`}
              rows="3"
              placeholder="Enter note for techs"
             >
             
             </textarea>
             {errors.note_for_tech && <p className="mt-1 text-sm text-red-600">{errors.note_for_tech.message}</p>}
            </div>
          <div className="space-y-4 mt-6  ">
            <label className="block text-sm font-medium text-gray-700">Completion Notes</label>
            <textarea
              {...register('completion_note')}
              className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.note_fo_tech ? 'border-red-500' : 'border-gray-300'
                  }`}
              rows="3"
              placeholder="Enter completion notes"
             >
             
             </textarea>
             {errors.completion_note && <p className="mt-1 text-sm text-red-600">{errors.completion_note.message}</p>}
          </div>

           
         </div>

      </div>


         {/* Tasks and Products Section */}
          
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className=" mt-6 bg-white rounded-lg shadow-md p-6">
           <h2 className="text-xl font-semibold text-gray-800 mb-2">Task List</h2>
           <hr className="border-t border-gray-200 mb-4" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Tasks</label>
                <button
                  type="button"
                  onClick={() => setShowTaskInput(true)}
                  className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
              </div>
              {showTaskInput && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Add new task"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddTask}
                    className="w-50 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {watch('tasks')?.map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={(e) => {
                          const updatedTasks = watch('tasks').map((t) =>
                            t.id === task.id ? { ...t, completed: e.target.checked } : t
                          );
                          setValue('tasks', updatedTasks);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className={`ml-2 ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {task.text}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedTasks = watch('tasks').filter((t) => t.id !== task.id);
                        setValue('tasks', updatedTasks);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className=" mt-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Add Notes</h2>
           <hr className="border-t border-gray-200 mb-4" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <button
                    type="button"
                    onClick={() => setShowNoteInput(true)}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </button>
                </div>

                 {showNoteInput && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add new note"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddNote}
                      className="w-50 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>
                )}

                <div className="space-y-2">
                {watch('notes')?.map((note) => (
                  <div key={note.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={note.completed}
                        onChange={(e) => {
                          const updatedNotes = watch('notes').map((t) =>
                            t.id === note.id ? { ...t, completed: e.target.checked } : t
                          );
                          setValue('notes', updatedNotes);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className={`ml-2 ${note.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {note.text}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedNotes = watch('notes').filter((t) => t.id !== note.id);
                        setValue('notes', updatedNotes);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
              </div>


          </div>
        </div>

        {/* Products, Driver, and Expenses Tabs */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 overflow-y-auto" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab('products')}
                  className={`${
                    activeTab === 'products'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Product & Services
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('driver')}
                  className={`${
                    activeTab === 'driver'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Driver & Labor Times
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('expenses')}
                  className={`${
                    activeTab === 'expenses'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Expenses
                </button>
              </nav>
            </div>

            {activeTab === 'products' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {lineItems.map((item) => (
                    <div key={item.id} className="grid grid-cols-1 gap-6 p-4 border border-gray-200 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={item.description}
                          onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter description"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Warehouse</label>
                          <select
                            value={item.warehouse}
                            onChange={(e) => handleLineItemChange(item.id, 'warehouse', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Warehouse</option>
                            <option value="warehouse1">Warehouse 1</option>
                            <option value="warehouse2">Warehouse 2</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Qty/Hrs</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter quantity"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Rate</label>
                            <input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleLineItemChange(item.id, 'rate', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter rate"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Total</label>
                            <input
                              type="number"
                              value={item.total}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter total"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Cost</label>
                          <input
                            type="number"
                            value={item.cost}
                            onChange={(e) => handleLineItemChange(item.id, 'cost', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter cost"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Margin</label>
                          <input
                            type="number"
                            value={item.margin}
                            onChange={(e) => handleLineItemChange(item.id, 'margin', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter margin"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Tax</label>
                          <select
                            value={item.tax}
                            onChange={(e) => handleLineItemChange(item.id, 'tax', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Select Tax"
                          >
                            <option value="">Select Tax</option>
                            <option value="10">10%</option>
                            <option value="20">20%</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => handleCopyLineItem(item.id)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(item.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Add Line Item
                  </button>
                  <div className="flex space-x-4">
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                         handleAddLineItem(value)
                          // Add logic to apply tax/discount to line items
                        }
                      }}
                    >
                      <option value="">Add Tax and Discount</option>
                      <option value="10">10% Tax</option>
                      <option value="20">20% Tax</option>
                      <option value="discount">Discount</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'driver' && (
              <div className="space-y-6">
                {driverLaborTimes.map((item) => (
                  <div key={item.id} className="grid grid-cols-1 gap-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tech</label>
                        <select
                          value={item.tech}
                          onChange={(e) => handleDriverLaborTimeChange(item.id, 'tech', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Tech</option>
                          {companyWorkForce.map((tech) => (
                            <option key={tech.id} value={tech.id}>
                              {tech.firstName} {tech.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => handleDriverLaborTimeChange(item.id, 'date', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Drive Time</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500">Start Time</label>
                            <input
                              type="time"
                              value={item.driveStartTime}
                              onChange={(e) => handleDriverLaborTimeChange(item.id, 'driveStartTime', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500">End Time</label>
                            <input
                              type="time"
                              value={item.driveEndTime}
                              onChange={(e) => handleDriverLaborTimeChange(item.id, 'driveEndTime', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Labor Time</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500">Start Time</label>
                            <input
                              type="time"
                              value={item.laborStartTime}
                              onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborStartTime', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500">End Time</label>
                            <input
                              type="time"
                              value={item.laborEndTime}
                              onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborEndTime', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Driver Hours/Minutes</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500">Hours</label>
                            <input
                              type="number"
                              value={item.driverHours}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500">Minutes</label>
                            <input
                              type="number"
                              value={item.driverMinutes}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Labor Hours/Minutes</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500">Hours</label>
                            <input
                              type="number"
                              value={item.laborHours}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500">Minutes</label>
                            <input
                              type="number"
                              value={item.laborMinutes}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Driver Rate</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.driverRate}
                            onChange={(e) => handleDriverLaborTimeChange(item.id, 'driverRate', e.target.value)}
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">/hr</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Labor Rate</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.laborRate}
                            onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborRate', e.target.value)}
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">/hr</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.billable}
                          onChange={(e) => handleDriverLaborTimeChange(item.id, 'billable', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">Billable</label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Total</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.total}
                            readOnly
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Cost</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.cost}
                            readOnly
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Margin</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.margin}
                            readOnly
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveDriverLaborTime(item.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={handleAddDriverLaborTime}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Add More
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'expenses' && (
              <div className="space-y-6">
                {expenses.map((item) => (
                  <div key={item.id} className="grid grid-cols-1 gap-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => handleExpenseChange(item.id, 'date', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <select
                          value={item.category}
                          onChange={(e) => handleExpenseChange(item.id, 'category', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Category</option>
                          {expenseCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Purchased From</label>
                        <input
                          type="text"
                          value={item.purchasedFrom}
                          onChange={(e) => handleExpenseChange(item.id, 'purchasedFrom', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter vendor name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => handleExpenseChange(item.id, 'amount', e.target.value)}
                            className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.billable}
                          onChange={(e) => handleExpenseChange(item.id, 'billable', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">Billable</label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Reimburse To</label>
                        <select
                          value={item.reimburseTo}
                          onChange={(e) => handleExpenseChange(item.id, 'reimburseTo', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Employee</option>
                          {companyWorkForce.map((employee) => (
                            <option key={employee.id} value={employee.id}>
                              {employee.firstName} {employee.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <textarea
                        value={item.notes}
                        onChange={(e) => handleExpenseChange(item.id, 'notes', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter notes"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Receipts</label>
                      <div className="mt-2">
                        <input
                          type="file"
                          multiple
                          onChange={(e) => handleReceiptUpload(item.id, Array.from(e.target.files))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {item.receipts.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {item.receipts.map((receipt, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                              <span className="text-sm text-gray-600">{receipt.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedReceipts = item.receipts.filter((_, i) => i !== index);
                                  handleExpenseChange(item.id, 'receipts', updatedReceipts);
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveExpense(item.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={handleAddExpense}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Add More
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Products, Services, Taxes & Fees</span>
                <span className="font-medium">${totals.productsTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Drive & Labor Time</span>
                <span className="font-medium">${totals.driverLaborTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Billable Expenses</span>
                <span className="font-medium">${totals.expensesTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Job Total</span>
                <span className="font-medium">${totals.jobTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payments/Deposits</span>
                <span className="font-medium">${totals.paymentsTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Total Due</span>
                <span className="font-medium">${totals.totalDue.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Job Cost</span>
                <span className="font-medium">${totals.jobCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Gross Profit ({totals.grossProfitPercentage.toFixed(2)}%)</span>
                <span className={`font-medium ${totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${totals.grossProfit.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {id ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                id ? 'Update Job' : 'Create Job'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JobForm;